import Head from "next/head";
import type { GetServerSidePropsResult, NextPage, NextPageContext } from "next";
import { TenantContext } from "../../../../shared/contexts/tenantContext";
import { getFrontendTenantConfigurationById } from "../../../../shared/contexts/tenantsConfigurations";
import { Layout } from "../../../../components/Layout";
import { WorkplaceProfile } from "../../../../components/WorkplaceProfile";
import * as React from "react";
import { useTranslations } from "../../../../shared/i18n/localization";
import { stringFromQueryParam } from "../../../../shared/helpers/urls";
import { tenantIdFromSlug } from "../../../../shared/helpers/tenants";
import { getCoreApi } from "../../../../shared/api/coreService/api";
import type { Workplace } from "../../../../shared/types";
import { useEffect } from "react";
import { sendToGoogleTagManager } from "../../../../shared/googleTagManager";

interface WorkplacePageProps {
  workplace: Workplace;
  tenantId: string;
}

const WorkplacePage: NextPage<WorkplacePageProps> = ({
  tenantId,
  workplace,
}) => {
  useEffect(() => {
    sendToGoogleTagManager({
      event: "generic_data",
      tenant_id: tenantId,
    });
  }, [tenantId]);

  return (
    <TenantContext.Provider
      value={getFrontendTenantConfigurationById(tenantId)}
    >
      <WorkplaceProfileHead tenantId={tenantId} workplace={workplace} />
      <main>
        <Layout>
          <WorkplaceProfile workplace={workplace} />
        </Layout>
      </main>
    </TenantContext.Provider>
  );
};

export async function getServerSideProps(
  context: NextPageContext
): Promise<GetServerSidePropsResult<WorkplacePageProps>> {
  const tenantSlug = stringFromQueryParam(context.query.tenantSlug);
  const slug = stringFromQueryParam(context.query.workplaceSlug);
  const tenantId = tenantIdFromSlug(tenantSlug || "");

  if (!tenantSlug || !slug || !tenantId) {
    return { notFound: true };
  }

  const coreApi = getCoreApi();
  const workplace = await coreApi.getWorkplace({ tenantId, slug });
  if (!workplace) {
    return { notFound: true };
  }

  const workplaceDoctors = await coreApi.getWorkplaceDoctors({
    tenantId,
    workplaceId: workplace.id,
  });

  workplace.doctors = workplaceDoctors.filter((doctor) => doctor.is_active);

  if (workplace.doctors.length === 0) {
    return { notFound: true };
  }

  return {
    props: { workplace, tenantId },
  };
}

interface WorkplaceProfileHeadProps {
  workplace: Workplace;
  tenantId: string;
}

const WorkplaceProfileHead: React.FC<WorkplaceProfileHeadProps> = ({
  tenantId,
  workplace,
}) => {
  const tenantConfiguration = getFrontendTenantConfigurationById(tenantId);
  const { translate, locale } = useTranslations();
  const workplaceName = workplace.name;
  return (
    <>
      <Head>
        <title>{`${workplaceName} | OnlineDoctor`}</title>
        <meta
          name="description"
          content={`${translate("workplaceProfile.title_content", {
            workplaceName: workplaceName,
          })} ${translate("workplaceProfile.description_content")}`}
        />
        <link
          rel="canonical"
          href={`${process.env.hostName}/${locale}/t/${tenantConfiguration.name}/w/${workplace.slug}`}
        />
        {tenantConfiguration.enabledPatientLocales.map((supportedLocale) => (
          <link
            key={supportedLocale}
            rel="alternate"
            hrefLang={supportedLocale}
            href={`${process.env.hostName}/${supportedLocale}/t/${tenantConfiguration.name}/w/${workplace.slug}`}
          />
        ))}
        <link rel="icon" href={`/${tenantConfiguration.name}/favicon.svg`} />
      </Head>
    </>
  );
};

export default WorkplacePage;
